import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { Router, RouterModule } from '@angular/router';

import { AppError, AppErrorCodes } from '@core/api';

import { SharedButtonsModule } from '@shared/buttons';

@Component({
  selector: 'la-page-error',
  templateUrl: './page-error.component.html',
  styleUrls: ['./page-error.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCardModule, MatButtonModule, RouterModule, SharedButtonsModule],
})
export class PageErrorComponent {
  @Input() error?: AppError;
  @Output() recover = new EventEmitter<void>();

  public readonly CODES = AppErrorCodes;
  private readonly router = inject(Router);

  get title(): string {
    if (this.error?.status === 404) {
      return 'Page not found';
    }

    if (this.error?.status && this.error.status >= 500) {
      return 'Something went wrong';
    }

    if (this.error?.status === 429) {
      return 'Too many requests';
    }

    switch (this.error?.code) {
      case this.CODES.NO_INTERNET:
        return 'No internet';
      case this.CODES.DEPRECATED:
        return 'New version available';
      default:
        return 'Error';
    }
  }

  get body(): string {
    if (this.error?.status === 404) {
      return "Let's get you back on the map.";
    }

    if (this.error?.status && this.error.status >= 500) {
      return "Sorry, we're having trouble loading this page. Please try again in a few minutes or contact us for support if the problem continues.";
    }

    if (this.error?.status === 429) {
      return "We're receiving too many requests at the moment. Please try again in a few minutes.";
    }

    switch (this.error?.code) {
      case this.CODES.NO_INTERNET:
        return "You're not connected to the internet at the moment, so any changes you make won't be saved. Please check your connection and try again.";
      case this.CODES.DEPRECATED:
        return 'Please refresh your browser or clear your cache to ensure you are using the latest version of the Land App.';
      default:
        return this.error?.message ?? 'Unknown error';
    }
  }

  navigateToMaps() {
    this.recover.emit();
    this.router.navigate(['/maps']);
  }
}
